import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./FacedownPaywall.module.scss"

/**
 * FacedownPaywall (`/facedown`) — an original, self-contained FEATURE-SPECIFIC
 * paywall for Private Photo Vault Premium. Paywall type rolled at random this
 * run: FEATURE-SPECIFIC.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run is `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority. The whole concept is built to it — one real security
 * feature, told through the single gesture that arms it.
 *
 * ── The feature ─────────────────────────────────────────────────────────────
 * FACE DOWN LOCK — a genuine Private Photo Vault Premium feature: "Instantly app
 * lock by flipping your phone." Lay your phone screen-down and the vault seals
 * itself. No button, no menu, no delay. It is the reflex you already have — the
 * instinct to cover a screen when someone leans in — turned into a hard lock.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "The lock that happens by instinct." Every other lock asks something of you:
 * a tap, a glance, a code, remembering. Face Down Lock asks for the one motion
 * you'd make anyway. Set the phone down and your photos are gone before anyone
 * can look. This paywall SELLS THAT ONE FEATURE — the star of the screen is the
 * gesture, not a bullet list.
 *
 * ── Interaction (the hero mechanic) ─────────────────────────────────────────
 * TILT-TO-LOCK. The hero is a real phone in perspective, screen full of your
 * memories, standing up on a tabletop. Drag it down and it physically tips
 * forward on its bottom edge (3D rotateX) toward the table; a tilt gauge sweeps
 * and a live status pill reads VISIBLE. The instant it passes the lock line the
 * screen SEALS — photos gone, a lock face snaps in, the pill flips to LOCKED —
 * faster than the tilt finishes. To see the photos again it demands YOU: a Face
 * ID scan sweeps and the phone springs back up. A physical tilt-and-seal — not a
 * tap-to-gather (Fold), a ring-sweep (Aperture), a card flip (Flipside), a dial,
 * a press-and-hold (Nightlatch) or a drag-key (Cipher).
 *
 * ── Divergence (vs. the 130+ prior concepts) ───────────────────────────────
 * Layout      : a single phone STANDING ON A TABLETOP with a tilt gauge — not a
 *               wall, door, folio, grid, keypad, lens barrel or exposure field.
 * Interaction : TILT-TO-LOCK — drag the device face-down through 3D rotation and
 *               watch it seal at the threshold. New to the set.
 * Metaphor    : the INSTINCTIVE COVER — laying a phone screen-down as a shield,
 *               a motion everyone already makes, now a real lock.
 * Value frame : SPEED / EFFORTLESSNESS — "locked before anyone can look," the
 *               guard you never have to remember. Not loss, fear or scarcity.
 * Palette     : calm slate tabletop, warm lamp glow on the photos, a cool
 *               steel-mint "locked" seal. Confident, quiet, tactile.
 * Register    : relief and control — the ease of a guard that runs on reflex.
 * CTA         : "Arm Face Down Lock" — yearly featured.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * Face Down Lock is a REAL feature (localized `features.faceDownLock` /
 * `summerSale.features.faceDownLock`: "Instantly App lock by flipping your
 * phone"). The supporting trust chips are real too (only-you lock with Face ID /
 * Touch ID / a code · Break-In Reports · encrypted Cloud Vault). NOTHING is ever
 * deleted and nothing leaves the phone — locking only hides the screen; there is
 * NO data-loss threat, NO countdown, NO fabricated scarcity. "Save 79%" is exact
 * arithmetic from live product data. Yearly is the featured, default, most
 * prominent price; monthly is small and secondary. The 7-day free trial is the
 * real trial. Renders on built-in debug product data with bundled photos — no
 * native injection, no external egress.
 */

// The memories on the phone's screen — bundled, self-contained repo photos.
const MEMORIES = [
  "/byheart/m03.jpg",
  "/byheart/m08.jpg",
  "/byheart/m12.jpg",
  "/byheart/m05.jpg",
  "/byheart/m14.jpg",
  "/byheart/m09.jpg",
]

// Real supporting Private Photo Vault safeguards — a compact trust row, NOT the
// star of the screen. Face Down Lock is the feature being sold.
const TRUST = [
  {
    key: "lock",
    Icon: svg.featureIcons.PatternLock,
    label: "Only-you lock",
    line: "Face ID · Touch ID · code",
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    label: "Break-In Reports",
    line: "A snapshot of a wrong try",
  },
  {
    key: "cloud",
    Icon: svg.featureIcons.CloudVault,
    label: "Encrypted Cloud Vault",
    line: "A private, encrypted backup",
  },
]

// Tilt threshold (0..1) at which the vault seals. Chosen so the screen is gone
// well before the phone is flat — "locked before anyone can look."
const LOCK_AT = 0.56
const MAX_TILT_DEG = 84 // rotateX at fully face-down

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v)
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3)

const FacedownPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  // ── pricing (yearly featured, monthly secondary) ──────────────────────────
  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savePercent = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    return Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── tilt-to-lock state ────────────────────────────────────────────────────
  const [tilt, setTilt] = useState(0) // 0 = upright/visible, 1 = face-down
  const [locked, setLocked] = useState(false)
  const [scanning, setScanning] = useState(false)
  const [animating, setAnimating] = useState(false) // css transition on/off
  const [hasTouched, setHasTouched] = useState(false)

  const stageRef = useRef<HTMLDivElement | null>(null)
  const dragRef = useRef<{ startY: number; startTilt: number } | null>(null)
  const rafRef = useRef<number | null>(null)
  const timers = useRef<number[]>([])

  const clearTimers = useCallback(() => {
    timers.current.forEach((t) => window.clearTimeout(t))
    timers.current = []
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = null
  }, [])

  // Smoothly animate `tilt` to a target, toggling the CSS transition flag.
  const animateTiltTo = useCallback(
    (to: number, ms: number, onDone?: () => void) => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      if (reduceMotion || ms <= 0) {
        setTilt(to)
        onDone?.()
        return
      }
      const from = tiltRef.current
      const start = performance.now()
      const step = (now: number) => {
        const t = clamp01((now - start) / ms)
        const v = from + (to - from) * easeOut(t)
        setTilt(v)
        if (t < 1) {
          rafRef.current = requestAnimationFrame(step)
        } else {
          rafRef.current = null
          onDone?.()
        }
      }
      rafRef.current = requestAnimationFrame(step)
    },
    [reduceMotion],
  )

  // keep a ref of latest tilt for the rAF closure
  const tiltRef = useRef(0)
  useLayoutEffect(() => {
    tiltRef.current = tilt
  }, [tilt])

  const seal = useCallback(() => {
    setLocked(true)
    setAnimating(true)
    animateTiltTo(1, 260)
    // haptic-style cue only; nothing destructive happens.
    if ("vibrate" in navigator) {
      try {
        navigator.vibrate?.(12)
      } catch {}
    }
  }, [animateTiltTo])

  const unlockWithFaceID = useCallback(() => {
    if (scanning) return
    setScanning(true)
    const t1 = window.setTimeout(() => {
      setScanning(false)
      setLocked(false)
      setAnimating(true)
      animateTiltTo(0, 520)
    }, 1050)
    timers.current.push(t1)
  }, [scanning, animateTiltTo])

  // ── auto-demo once on mount (skipped when reduced motion) ─────────────────
  useEffect(() => {
    if (reduceMotion) return
    setAnimating(true)
    const t0 = window.setTimeout(() => {
      // tip down toward the table…
      animateTiltTo(1, 900, () => {
        setLocked(true) // …and seal at the bottom of the tip
      })
    }, 820)
    const t1 = window.setTimeout(() => {
      // then prove only YOU reopen it
      setScanning(true)
    }, 2150)
    const t2 = window.setTimeout(() => {
      setScanning(false)
      setLocked(false)
      animateTiltTo(0, 620)
    }, 3250)
    timers.current.push(t0, t1, t2)
    return clearTimers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => clearTimers, [clearTimers])

  // ── pointer drag on the stage → tilt the phone ────────────────────────────
  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (locked || scanning) return
      clearTimers()
      setHasTouched(true)
      setAnimating(false)
      dragRef.current = { startY: e.clientY, startTilt: tiltRef.current }
      ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
    },
    [locked, scanning, clearTimers],
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const d = dragRef.current
      if (!d) return
      const stageH = stageRef.current?.clientHeight ?? 360
      // drag DOWN increases tilt; travel ≈ 62% of stage height for full tip.
      const delta = (e.clientY - d.startY) / (stageH * 0.62)
      const next = clamp01(d.startTilt + delta)
      setTilt(next)
      if (next >= LOCK_AT) {
        dragRef.current = null
        seal()
      }
    },
    [seal],
  )

  const onPointerUp = useCallback(() => {
    const d = dragRef.current
    dragRef.current = null
    if (!d) return
    // released before the lock line → ease back up, still visible.
    setAnimating(true)
    animateTiltTo(0, 420)
  }, [animateTiltTo])

  const purchase = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  // ── derived geometry ──────────────────────────────────────────────────────
  const tiltDeg = tilt * MAX_TILT_DEG
  const lift = tilt * 26 // px the phone slides toward the table as it tips
  const shadow = 0.18 + tilt * 0.5 // contact shadow strength
  const shadowScale = 0.4 + tilt * 0.62
  const gaugeAngle = -52 + tilt * 104 // needle sweep across the arc
  const pastLine = tilt >= LOCK_AT

  const statusLabel = locked ? "LOCKED" : "VISIBLE"

  const stageClass = [
    styles.stage,
    locked ? styles.isLocked : "",
    animating ? styles.animating : "",
    scanning ? styles.scanning : "",
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <div className={styles.page}>
      <div className={styles.ambient} aria-hidden="true" />

      <button
        className={styles.close}
        onClick={() => native.postNativeEvent("close")}
        aria-label="Close"
      >
        <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
          <path
            d="M5 5l14 14M19 5L5 19"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      </button>

      <div className={styles.scroll}>
        {/* ── header ─────────────────────────────────────────────────────── */}
        <header className={styles.head}>
          <div className={styles.kicker}>
            <span className={styles.kickerDot} aria-hidden="true">
              <svg.featureIcons.FacedownLock />
            </span>
            <span>Private Photo Vault · Face Down Lock</span>
          </div>
          <h1 className={styles.title}>
            The lock that happens
            <br />
            <span className={styles.titleAccent}>by instinct.</span>
          </h1>
          <p className={styles.sub}>
            Lay your phone screen-down and the vault seals itself — the one
            motion you'd make anyway, turned into a lock.
          </p>
        </header>

        {/* ── hero: tilt-to-lock ─────────────────────────────────────────── */}
        <div className={styles.heroWrap}>
          <div className={styles.statusRow}>
            <span
              className={`${styles.statusPill} ${
                locked ? styles.pillLocked : styles.pillVisible
              }`}
            >
              <span className={styles.pillDot} aria-hidden="true" />
              {statusLabel}
            </span>
            <span className={styles.gauge} aria-hidden="true">
              <svg viewBox="0 0 120 66" width="120" height="66">
                <path
                  d="M12 60 A48 48 0 0 1 108 60"
                  fill="none"
                  stroke="var(--fd-arc)"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <path
                  className={styles.gaugeHot}
                  d="M12 60 A48 48 0 0 1 108 60"
                  fill="none"
                  stroke="var(--fd-mint)"
                  strokeWidth="3"
                  strokeLinecap="round"
                  style={{
                    strokeDasharray: 151,
                    strokeDashoffset: 151 * (1 - clamp01(tilt)),
                    opacity: pastLine ? 1 : 0.55,
                  }}
                />
                {/* lock-line tick */}
                <g
                  transform={`rotate(${-52 + LOCK_AT * 104} 60 60)`}
                  opacity="0.9"
                >
                  <line
                    x1="60"
                    y1="18"
                    x2="60"
                    y2="26"
                    stroke="var(--fd-mint)"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </g>
                <g transform={`rotate(${gaugeAngle} 60 60)`}>
                  <line
                    x1="60"
                    y1="60"
                    x2="60"
                    y2="24"
                    stroke="var(--fd-needle)"
                    strokeWidth="3.5"
                    strokeLinecap="round"
                  />
                  <circle cx="60" cy="60" r="5" fill="var(--fd-needle)" />
                </g>
              </svg>
              <span className={styles.gaugeLabel}>
                {pastLine ? "sealed" : "tip to seal"}
              </span>
            </span>
          </div>

          <div
            ref={stageRef}
            className={stageClass}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            role="button"
            tabIndex={0}
            aria-label="Tilt the phone face-down to lock the vault"
          >
            {/* tabletop */}
            <div className={styles.table} aria-hidden="true">
              <div
                className={styles.contact}
                style={{
                  opacity: shadow,
                  transform: `translate(-50%, 0) scaleX(${shadowScale})`,
                }}
              />
            </div>

            {/* the phone */}
            <div
              className={styles.phonePivot}
              style={{
                transform: `translateY(${lift}px) rotateX(${tiltDeg}deg)`,
              }}
            >
              <div className={styles.phone}>
                <div className={styles.notch} aria-hidden="true" />

                {/* photos side (visible) */}
                <div className={styles.screen}>
                  <div className={styles.photoGrid}>
                    {MEMORIES.map((src, i) => (
                      <div className={styles.photo} key={src}>
                        <img src={src} alt="" draggable={false} />
                      </div>
                    ))}
                  </div>
                  <div className={styles.screenGlow} aria-hidden="true" />
                </div>

                {/* lock face (revealed once sealed) */}
                <div className={styles.lockFace} aria-hidden={!locked}>
                  <div className={styles.lockBadge}>
                    <svg viewBox="0 0 32 32" width="30" height="30">
                      <rect
                        x="7"
                        y="14"
                        width="18"
                        height="13"
                        rx="3.2"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                      />
                      <path
                        d="M11 14v-3a5 5 0 0 1 10 0v3"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                      />
                      <circle cx="16" cy="20.5" r="1.9" fill="currentColor" />
                    </svg>
                  </div>
                  <span className={styles.lockWord}>Locked</span>
                  <span className={styles.lockHint}>
                    Photos hidden — nothing deleted
                  </span>

                  {locked && (
                    <button
                      className={styles.faceId}
                      onClick={(e) => {
                        e.stopPropagation()
                        unlockWithFaceID()
                      }}
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16">
                        <path
                          d="M4 8V6a2 2 0 0 1 2-2h2M16 4h2a2 2 0 0 1 2 2v2M20 16v2a2 2 0 0 1-2 2h-2M8 20H6a2 2 0 0 1-2-2v-2"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.7"
                          strokeLinecap="round"
                        />
                        <path
                          d="M9 10v1.5M15 10v1.5M12 10v3l-1 .8M9.5 15c1.5 1.1 3.5 1.1 5 0"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                        />
                      </svg>
                      {scanning ? "Face ID…" : "Face ID to open"}
                    </button>
                  )}

                  <div className={styles.scanLine} aria-hidden="true" />
                </div>
              </div>
            </div>

            {/* drag hint */}
            {!hasTouched && !locked && (
              <div className={styles.hint} aria-hidden="true">
                <span className={styles.hintArrow}>↓</span>
                Lay it face-down
              </div>
            )}
          </div>

          <p className={styles.heroCaption}>
            {locked
              ? "Sealed. Only you reopen it — Face ID, Touch ID or your code."
              : "Drag the phone down. It's gone before anyone can lean in and look."}
          </p>
        </div>

        {/* ── why this feature (three quiet proofs) ──────────────────────── */}
        <ul className={styles.proofs}>
          <li>
            <span className={styles.proofNum}>0</span>
            <span>
              <b>Zero taps</b>
              <em>No button, no menu — the gesture is the lock.</em>
            </span>
          </li>
          <li>
            <span className={styles.proofNum}>&lt;1s</span>
            <span>
              <b>Instant</b>
              <em>Sealed the moment the screen turns away.</em>
            </span>
          </li>
          <li>
            <span className={styles.proofInf}>∞</span>
            <span>
              <b>Nothing lost</b>
              <em>Photos are hidden, never deleted or moved.</em>
            </span>
          </li>
        </ul>

        {/* ── supporting trust row (secondary) ───────────────────────────── */}
        <div className={styles.trust}>
          <span className={styles.trustLead}>
            Comes with your Premium vault
          </span>
          <div className={styles.trustRow}>
            {TRUST.map((t) => (
              <div className={styles.trustChip} key={t.key}>
                <span className={styles.trustIcon} aria-hidden="true">
                  <t.Icon />
                </span>
                <span className={styles.trustText}>
                  <b>{t.label}</b>
                  <em>{t.line}</em>
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── pricing: yearly featured, monthly small & secondary ────────── */}
        <div className={styles.pricing}>
          <button
            className={`${styles.yearly} ${
              plan === "yearly" ? styles.selected : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            <span className={styles.yearlyGlow} aria-hidden="true" />
            <span className={styles.yearlyTop}>
              <span className={styles.yearlyName}>Yearly</span>
              <span className={styles.trialTag}>
                {trialDays}-day free trial
              </span>
            </span>
            <span className={styles.yearlyPriceRow}>
              <span className={styles.yearlyPrice}>{yearlyPrice}</span>
              <span className={styles.yearlyUnit}>/ year</span>
              <span className={styles.yearlyPerMo}>≈ {yearlyPerMonth}/mo</span>
            </span>
            <span className={styles.yearlyFoot}>
              <span className={styles.saveBadge}>Save {savePercent}%</span>
              <span className={styles.bestLabel}>Best value · recommended</span>
            </span>
          </button>

          <button
            className={`${styles.monthly} ${
              plan === "monthly" ? styles.monthlySel : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span>Or monthly</span>
            <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
          </button>
        </div>

        <button className={styles.cta} onClick={purchase}>
          <span className={styles.ctaMain}>
            {plan === "yearly"
              ? `Arm Face Down Lock — ${trialDays}-day free trial`
              : "Arm Face Down Lock"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `Then ${yearlyPrice}/year · cancel anytime`
              : `${monthlyPrice}/month · cancel anytime`}
          </span>
        </button>

        <div className={styles.footer}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span aria-hidden="true">·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span aria-hidden="true">·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default FacedownPaywall
